import { useEffect, useRef } from 'react';
import ApiService, { ICountryData } from '../services/api';
import { cookieHelpers } from '../utils/cookieUtils';

// Country to language mapping
const defaultLanguage: Record<string, string> = {
  "Albania": "Albanian",
  "Austria": "German",
  "Belgium": "Dutch",
  "Bosnia-Herzegovina": "Bosnian",
  "Botswana": "English",
  "Bulgaria": "Bulgarian",
  "Croatia": "Croatian",
  "Cyprus": "Greek",
  "Czech Republic": "Czech",
  "Denmark": "Danish",
  "Estonia": "Estonian",
  "Finland": "Finnish",
  "France": "French",
  "French Polynesia": "French",
  "Germany": "German",
  "Ghana": "English",
  "Greece": "Greek",
  "Hungary": "Hungarian",
  "Iceland": "Icelandic",
  "Ireland": "English",
  "Israel": "Hebrew",
  "Italy": "Italian",
  "Kosovo": "Albanian",
  "Latvia": "Latvian",
  "Lesotho": "English",
  "Lithuania": "Lithuanian",
  "Macedonia": "Macedonian",
  "Malta": "English",
  "Moldova": "Romanian",
  "Montenegro": "Montenegrin",
  "Namibia": "English",
  "Netherlands": "Dutch",
  "North Macedonia": "Macedonian",
  "Norway": "Norwegian",
  "Poland": "Polish",
  "Portugal": "Portuguese",
  "Romania": "Romanian",
  "Serbia": "Serbian",
  "Slovak Republic": "Slovak",
  "Slovakia": "Slovak",
  "Slovenia": "Slovene",
  "South Africa": "English",
  "Spain": "Spanish",
  "Swaziland": "English",
  "Eswatini": "English",
  "Sweden": "Swedish",
  "Switzerland": "German",
  "Turkey": "Turkish",
  "Ukraine": "Ukrainian",
  "United Kingdom": "English",
};

interface UseIpLanguageDetectionProps {
  languages: ICountryData[];
  loading: boolean;
  flowType: 'distributor' | 'customer';
  onLanguageDetected: (languageCode: string) => Promise<void>;
  onLanguageSet: (languageCode: string) => void;
}

export const useIpLanguageDetection = ({
  languages,
  loading,
  flowType,
  onLanguageDetected,
  onLanguageSet,
}: UseIpLanguageDetectionProps) => {
  const ipDetectionAttempted = useRef<boolean>(false);
  const apiService = useRef(new ApiService());

  useEffect(() => {
    if (ipDetectionAttempted.current) {
      return;
    }

    // Skip IP detection when the user has already chosen a language (including English)
    if (cookieHelpers.hasLanguage(flowType)) {
      return;
    }

    if (languages.length === 0) {
      return;
    }

    if (loading) {
      return;
    }
    
    if (languages.length > 0 && !loading) {
      ipDetectionAttempted.current = true;
      
      const currentLanguages = languages;
      
      const detectLanguageFromIp = async () => {
        try {
          const timeoutPromise = new Promise<null>((resolve) => {
            setTimeout(() => resolve(null), 3000);
          });

          const countryNamePromise = apiService.current.getCountryNameFromIp();
          const countryName = await Promise.race([countryNamePromise, timeoutPromise]);
          
          if (!countryName) {
            const englishLanguage = currentLanguages.find(
              (lang) => lang.country_language?.toLowerCase() === 'english' || lang.country_language_code === 'en'
            );
            
            if (englishLanguage && englishLanguage.country_language_code) {
              onLanguageSet(englishLanguage.country_language_code);
              await onLanguageDetected(englishLanguage.country_language_code);
            } else {
              onLanguageSet('en');
              await onLanguageDetected('en');
            }
            return;
          }
          
          if (countryName) {
            let normalizedCountryName = countryName.trim();
            
            if (normalizedCountryName.toLowerCase().startsWith('the ')) {
              normalizedCountryName = normalizedCountryName.substring(4).trim();
            }
            
            let targetLanguage = defaultLanguage[normalizedCountryName];
            
            if (!targetLanguage) {
              const countryVariations: Record<string, string> = {
                'UK': 'United Kingdom',
                'Great Britain': 'United Kingdom',
                'GB': 'United Kingdom',
                'The Netherlands': 'Netherlands',
                'The United Kingdom': 'United Kingdom',
              };
              
              const mappedCountry = countryVariations[countryName.trim()] || countryVariations[normalizedCountryName];
              if (mappedCountry) {
                targetLanguage = defaultLanguage[mappedCountry];
              }
            }
            
            if (targetLanguage) {
              let matchedLanguage = currentLanguages.find(
                (lang) => lang.country_language?.toLowerCase() === targetLanguage.toLowerCase()
              );

              if (!matchedLanguage && targetLanguage.toLowerCase() === 'english') {
                matchedLanguage = currentLanguages.find(
                  (lang) => lang.country_language_code?.toLowerCase() === 'en'
                );
              }
              
              if (matchedLanguage && matchedLanguage.country_language_code) {
                onLanguageSet(matchedLanguage.country_language_code);
                await onLanguageDetected(matchedLanguage.country_language_code);
                return;
              } 
            } 
          }
          
          if (!countryName) {
            const englishLanguage = currentLanguages.find(
              (lang) => lang.country_language?.toLowerCase() === 'english' || lang.country_language_code === 'en'
            );
            
            if (englishLanguage && englishLanguage.country_language_code) {
              onLanguageSet(englishLanguage.country_language_code);
              await onLanguageDetected(englishLanguage.country_language_code);
            } else {
              onLanguageSet('en');
              await onLanguageDetected('en');
            }
          }
        } catch (error) {
          console.error('[useIpLanguageDetection] Error detecting language from IP:', error);
          const englishLanguage = currentLanguages.find(
            (lang) => lang.country_language?.toLowerCase() === 'english' || lang.country_language_code === 'en'
          );
          
          if (englishLanguage && englishLanguage.country_language_code) {
            onLanguageSet(englishLanguage.country_language_code);
            await onLanguageDetected(englishLanguage.country_language_code);
          } else {
            onLanguageSet('en');
            await onLanguageDetected('en');
          }
        }
      };
      
      detectLanguageFromIp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages.length, loading]);
};
